import { AlertTriangle, BarChart3, CheckCircle2, Clock3, Cloud, Eye, History, LayoutDashboard, LockKeyhole, RefreshCw, Search, ShieldCheck, Swords, Trophy, UserRound, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDivisionLabel } from '../online/league';
import {
  activateOnlineProfile,
  cancelOnlineQueue,
  clearOnlineLeaderboardCache,
  getOnlinePortal,
  getOnlineQueueState,
  getOnlineReplay,
  joinOnlineQueue,
  listOnlineMatchHistory,
  subscribeToOnlineTicket,
  type OnlineMatchHistoryItem,
  type OnlinePortalData,
  type OnlineQueueState,
} from '../services/onlineLeague';
import type { MatchResult } from '../types';
import { OnlinePublicProfilePanel } from './OnlinePublicProfilePanel';
import { OnlineRankingsPanel } from './OnlineRankingsPanel';
import { OnlineReplayPage } from './OnlineReplayPage';
import { OnlineTournamentsPanel } from './OnlineTournamentsPanel';

interface OnlinePageProps {
  gameSaveId: string;
  managerName: string;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value));
}

function formatWait(startedAt: string, now: number) {
  const seconds = Math.max(0, Math.floor((now - Date.parse(startedAt)) / 1_000));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

export function OnlinePage({ gameSaveId, managerName }: OnlinePageProps) {
  const [section, setSection] = useState<'portal' | 'rankings' | 'profile' | 'tournaments'>('portal');
  const [selectedPublicProfileId, setSelectedPublicProfileId] = useState<string | null>(null);
  const [portal, setPortal] = useState<OnlinePortalData | null>(null);
  const [queue, setQueue] = useState<OnlineQueueState | null>(null);
  const [history, setHistory] = useState<OnlineMatchHistoryItem[]>([]);
  const [alias, setAlias] = useState(managerName.slice(0, 24));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replayLoadingId, setReplayLoadingId] = useState<string | null>(null);
  const [replay, setReplay] = useState<MatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError(null);
    try {
      const nextPortal = await getOnlinePortal();
      const [nextQueue, nextHistory] = nextPortal.profile
        ? await Promise.all([
          getOnlineQueueState(),
          listOnlineMatchHistory(nextPortal.profile.publicId),
        ])
        : [null, []];
      setPortal(nextPortal);
      setQueue(nextQueue);
      setHistory(nextHistory);
      if (nextQueue?.ticketStatus === 'COMPLETED') clearOnlineLeaderboardCache();
      if (nextPortal.profile) setAlias(nextPortal.profile.alias);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se ha podido abrir el portal online.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const awaitingServer = portal?.snapshot?.validationStatus === 'PENDING'
    || portal?.snapshot?.validationStatus === 'VALIDATING'
    || queue?.ticketStatus === 'SEARCHING'
    || queue?.ticketStatus === 'MATCHED'
    || queue?.matchStatus === 'SIMULATING';

  useEffect(() => {
    if (!awaitingServer) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, [awaitingServer]);

  useEffect(() => {
    if (!portal?.profile?.id) return;
    return subscribeToOnlineTicket(portal.profile.id, () => { void load(true); });
  }, [load, portal?.profile?.id]);

  useEffect(() => {
    if (!awaitingServer) return;
    let cancelled = false;
    let timer: number | undefined;
    let delay = 2_000;
    const poll = async () => {
      await load(true);
      if (cancelled) return;
      delay = Math.min(10_000, Math.round(delay * 1.5));
      timer = window.setTimeout(() => { void poll(); }, delay);
    };
    timer = window.setTimeout(() => { void poll(); }, delay);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [awaitingServer, load]);

  const matches = portal?.rank
    ? portal.rank.wins + portal.rank.draws + portal.rank.losses
    : 0;
  const progress = useMemo(() => {
    if (!portal?.rank) return 0;
    const threshold = portal.config.promotionThresholdByDivision[portal.rank.division];
    if (!threshold) return 100;
    return Math.max(0, Math.min(100, portal.rank.divisionPoints / threshold * 100));
  }, [portal]);

  const perform = async (action: () => Promise<void>, fallback: string) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      await load(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : fallback);
    } finally {
      setSaving(false);
    }
  };

  const activate = () => perform(
    async () => {
      await activateOnlineProfile(gameSaveId, alias);
      clearOnlineLeaderboardCache();
    },
    'No se ha podido vincular la plantilla.',
  );
  const startSearch = () => perform(joinOnlineQueue, 'No se ha podido iniciar la búsqueda.');
  const cancelSearch = () => perform(cancelOnlineQueue, 'No se ha podido cancelar la búsqueda.');

  const openReplay = async (matchId: string) => {
    setReplayLoadingId(matchId);
    setError(null);
    try {
      const envelope = await getOnlineReplay(matchId);
      setReplay(envelope.matchResult);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se ha podido abrir el replay.');
    } finally {
      setReplayLoadingId(null);
    }
  };

  const openPublicProfile = (publicPlayerId: string) => {
    setSelectedPublicProfileId(publicPlayerId);
    setSection('profile');
  };

  if (replay) return <OnlineReplayPage result={replay} onExit={() => setReplay(null)} />;
  if (loading) return <main className="page online-page"><div className="online-loading card"><RefreshCw className="spin" size={22} /> Cargando competición segura…</div></main>;

  const queueIsActive = queue?.ticketStatus === 'SEARCHING' || queue?.ticketStatus === 'MATCHED';

  return <main className="page online-page">
    <header className="page-heading online-heading">
      <div><span className="eyebrow">COMPETICIÓN ASÍNCRONA</span><h1>Portal Online</h1><p>Prepara tu once; el servidor resuelve cada partido una sola vez y ambos entrenadores reciben el mismo replay.</p></div>
      {portal?.season && <span className="online-season-chip"><Cloud size={15} /><span><small>TEMPORADA ACTIVA</small><b>{portal.season.name}</b></span></span>}
    </header>

    <nav className="online-section-nav" aria-label="Secciones del portal online">
      <button className={section === 'portal' ? 'active' : ''} onClick={() => setSection('portal')}><LayoutDashboard size={15} /> Liga Online</button>
      <button className={section === 'rankings' ? 'active' : ''} onClick={() => setSection('rankings')}><BarChart3 size={15} /> Clasificaciones</button>
      <button className={section === 'tournaments' ? 'active' : ''} onClick={() => setSection('tournaments')}><Trophy size={15} /> Torneos</button>
      {portal?.profile && <button className={section === 'profile' && selectedPublicProfileId === portal.profile.publicId ? 'active' : ''} onClick={() => openPublicProfile(portal.profile!.publicId)}><UserRound size={15} /> Mi perfil</button>}
    </nav>

    {error && <div className="online-message online-message--error" role="alert"><AlertTriangle size={18} /><span>{error}</span><button onClick={() => void load()}>Reintentar</button></div>}

    {section === 'rankings' && <OnlineRankingsPanel currentSeasonId={portal?.season?.id} ownPublicPlayerId={portal?.profile?.publicId} onOpenProfile={openPublicProfile} />}
    {section === 'profile' && selectedPublicProfileId && <OnlinePublicProfilePanel publicPlayerId={selectedPublicProfileId} isOwnProfile={selectedPublicProfileId === portal?.profile?.publicId} onBack={() => setSection('rankings')} />}
    {section === 'tournaments' && <OnlineTournamentsPanel seasonId={portal?.season?.id} hasProfile={!!portal?.profile} />}

    {section === 'portal' && <>

    {!error && !portal?.season && <section className="online-empty card">
      <AlertTriangle size={27} />
      <h2>No hay una temporada online activa</h2>
      <p>La navegación local sigue disponible. Un administrador debe abrir una temporada antes de registrar plantillas o disputar encuentros.</p>
    </section>}

    {portal?.season && !portal.profile && <section className="online-enrolment card">
      <div className="online-enrolment-copy"><span className="card-label"><ShieldCheck size={15} /> ALTA COMPETITIVA</span><h2>Vincula esta partida de forma explícita</h2><p>Se congelará la última versión guardada en la nube del once, la formación, las tácticas y el desarrollo necesario para competir. Tu carrera no se convierte en online ni puede ser modificada por un rival.</p></div>
      <div className="online-enrolment-form"><label htmlFor="online-alias">Alias público</label><input id="online-alias" value={alias} maxLength={24} onChange={(event) => setAlias(event.target.value)} /><small>No se publica tu email ni el identificador interno de la cuenta.</small><button className="primary-action" disabled={saving || alias.trim().length < 3} onClick={() => void activate()}>{saving ? <RefreshCw className="spin" size={15} /> : <LockKeyhole size={15} />}{saving ? 'Vinculando…' : 'Vincular plantilla'}</button></div>
    </section>}

    {portal?.season && portal.profile && portal.rank && <>
      <section className="online-rank-grid">
        <article className="online-division-card card"><span className="card-label"><Trophy size={15} /> DIVISIÓN ACTUAL</span><h2>{getDivisionLabel(portal.rank.division)}</h2><strong>{portal.rank.divisionPoints} <small>PTS</small></strong><div className="online-progress" aria-label={`Progreso de división: ${Math.round(progress)} %`}><i style={{ width: `${progress}%` }} /></div><p>{portal.rank.division === 'MUNDIAL' ? 'Has alcanzado la división más alta.' : `${Math.round(progress)}% del umbral de ascenso · mínimo ${portal.config.minimumMatchesBeforePromotion} partidos`}</p></article>
        <article className="online-stat-card card"><span>PARTIDOS</span><strong>{matches}</strong><small>{portal.rank.wins} V · {portal.rank.draws} E · {portal.rank.losses} D</small></article>
        <article className="online-stat-card card"><span>MEJOR DIVISIÓN</span><strong>{getDivisionLabel(portal.rank.bestDivision)}</strong><small>{portal.rank.lastMatchAt ? `Último partido ${formatDate(portal.rank.lastMatchAt)}` : 'Sin partidos disputados'}</small></article>
      </section>

      <section className="online-snapshot card"><div><span className="card-label"><CheckCircle2 size={15} /> PLANTILLA CONGELADA</span><h2>{portal.profile.alias}</h2><p>Guarda primero los cambios de carrera. Cada actualización crea un snapshot inmutable; una búsqueda iniciada conserva exactamente el snapshot con el que entró.</p></div><div className="online-snapshot-actions"><SnapshotStatus portal={portal} /><button className="ghost-action" disabled={saving || queueIsActive} onClick={() => void activate()}>{saving ? <RefreshCw className="spin" size={14} /> : <RefreshCw size={14} />} Actualizar plantilla</button></div></section>

      <MatchmakingPanel portal={portal} queue={queue} now={now} saving={saving} onSearch={() => void startSearch()} onCancel={() => void cancelSearch()} onReplay={openReplay} replayLoadingId={replayLoadingId} />

      <section className="online-history card">
        <header><span className="card-label"><History size={15} /> HISTORIAL RECIENTE</span><small>{history.length ? `${history.length} resultados verificados` : 'Todavía no hay resultados'}</small></header>
        {history.length ? <div className="online-history-list">{history.map((match) => <article key={match.id}>
          <time>{formatDate(match.settledAt)}</time><span><b>{match.homeAlias}</b><strong>{match.homeGoals} — {match.awayGoals}</strong><b>{match.awayAlias}</b></span><button className="ghost-action" disabled={replayLoadingId === match.id} onClick={() => void openReplay(match.id)}>{replayLoadingId === match.id ? <RefreshCw className="spin" size={13} /> : <Eye size={13} />} Replay</button>
        </article>)}</div> : <div className="online-history-empty">Los partidos liquidados por el worker aparecerán aquí.</div>}
      </section>
    </>}
    </>}
  </main>;
}

function SnapshotStatus({ portal }: { portal: OnlinePortalData }) {
  const snapshot = portal.snapshot;
  if (!snapshot) return <span className="online-validation pending"><Clock3 size={13} /> Sin validación</span>;
  if (snapshot.validationStatus === 'VALID') return <span className="online-validation valid"><CheckCircle2 size={13} /> Validada · fuerza {snapshot.squadPower}</span>;
  if (snapshot.validationStatus === 'REJECTED') return <span className="online-validation rejected"><XCircle size={13} /> Rechazada · {snapshot.validationError ?? 'plantilla inválida'}</span>;
  return <span className="online-validation pending"><RefreshCw className="spin" size={13} /> {snapshot.validationStatus === 'VALIDATING' ? 'Validando en servidor' : 'Pendiente del worker'}</span>;
}

function MatchmakingPanel({ portal, queue, now, saving, onSearch, onCancel, onReplay, replayLoadingId }: {
  portal: OnlinePortalData;
  queue: OnlineQueueState | null;
  now: number;
  saving: boolean;
  onSearch(): void;
  onCancel(): void;
  onReplay(matchId: string): Promise<void>;
  replayLoadingId: string | null;
}) {
  const valid = portal.snapshot?.validationStatus === 'VALID';
  if (queue?.ticketStatus === 'SEARCHING') return <section className="online-matchmaking card searching">
    <span className="online-matchmaking-icon"><Search size={24} /></span><div><span className="card-label">BUSCANDO RIVAL</span><h2>Priorizando un emparejamiento justo</h2><p>Misma división y MMR cercano primero. La tolerancia se amplía gradualmente sin superar los límites configurados.</p></div><div className="online-search-clock"><strong>{formatWait(queue.enteredAt, now)}</strong><small>TIEMPO EN COLA</small><button className="ghost-action" disabled={saving} onClick={onCancel}><XCircle size={14} /> Cancelar</button></div>
  </section>;
  if (queue?.ticketStatus === 'MATCHED' || queue?.matchStatus === 'SIMULATING') return <section className="online-matchmaking card matched">
    <span className="online-matchmaking-icon"><Swords size={24} /></span><div><span className="card-label">RIVAL ENCONTRADO</span><h2>{queue.opponentAlias ? `Contra ${queue.opponentAlias}` : 'Preparando partido'}</h2><p>Las plantillas ya están congeladas. El worker está ejecutando el motor autoritativo; cerrar esta pantalla no cancela el encuentro.</p></div><div className="online-worker-state"><RefreshCw className="spin" size={17} /><b>SIMULANDO</b><small>Reintento seguro activo</small></div>
  </section>;
  if (queue?.ticketStatus === 'COMPLETED' && queue.matchId) return <section className="online-matchmaking card settled">
    <span className="online-matchmaking-icon"><Trophy size={24} /></span><div><span className="card-label">PARTIDO LIQUIDADO</span><h2>{queue.homeGoals} — {queue.awayGoals} · {queue.opponentAlias}</h2><p>Variación de liga: <strong className={(queue.pointDelta ?? 0) >= 0 ? 'positive' : 'negative'}>{(queue.pointDelta ?? 0) >= 0 ? '+' : ''}{queue.pointDelta ?? 0} puntos</strong>. El marcador, el ranking y el replay se confirmaron en una sola transacción.</p></div><div className="online-match-actions"><button className="ghost-action" disabled={replayLoadingId === queue.matchId} onClick={() => void onReplay(queue.matchId!)}>{replayLoadingId === queue.matchId ? <RefreshCw className="spin" size={14} /> : <Eye size={14} />} Ver replay</button><button className="primary-action" disabled={!valid || saving} onClick={onSearch}><Search size={14} /> Buscar otro</button></div>
  </section>;
  if (queue?.ticketStatus === 'FAILED' || queue?.matchStatus === 'FAILED') return <section className="online-matchmaking card failed">
    <span className="online-matchmaking-icon"><AlertTriangle size={24} /></span><div><span className="card-label">SIMULACIÓN NO COMPLETADA</span><h2>El partido terminó sin conceder puntos</h2><p>Código: {queue.failureCode ?? 'SIMULATION_FAILED'}. Puedes iniciar otra búsqueda; ningún resultado parcial fue liquidado.</p></div><button className="primary-action" disabled={!valid || saving} onClick={onSearch}><RefreshCw size={14} /> Reintentar búsqueda</button>
  </section>;
  return <section className="online-matchmaking card ready">
    <span className="online-matchmaking-icon"><Swords size={24} /></span><div><span className="card-label">LIGA ONLINE</span><h2>{valid ? 'Tu equipo está listo' : 'Esperando validación autoritativa'}</h2><p>{valid ? `El sistema usará MMR como criterio principal y la fuerza ${portal.snapshot?.squadPower} como protección ante diferencias extremas.` : 'El worker debe validar jugadores, formación, técnicas, TP y desarrollo antes de permitir la entrada en cola.'}</p></div><button className="primary-action" disabled={!valid || saving} onClick={onSearch}>{saving ? <RefreshCw className="spin" size={14} /> : <Search size={14} />}{saving ? 'Entrando…' : 'Buscar rival'}</button>
  </section>;
}
